// Funnels screen.
//
// Two modes:
//   input  → brand picker + list of existing funnels (cards). New funnels
//            are generated via the Wizard / Building flow, so this screen
//            is primarily a viewer + editor.
//   result → V03 layout: header → step-card sequence → split layout
//            (browser preview on left, chat-edit panel on right). Edits
//            stream through the appropriate legacy or tenant edit route and
//            preserve source only; custom lead-site hosting is not configured.
//
// Deep-link via #/funnels?brand=<slug>&funnel=<slug>.

import Beacon from '../components/Beacon.jsx';
import { useEffect, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Card, Pill, Button } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { api, getToken } from '../api.js';
import SaasFunnelWorkbench from './SaasFunnelWorkbench.jsx';

export const FUNNEL_PREVIEW_SANDBOX = 'allow-forms allow-scripts allow-popups';

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

export function normalizeSaasFunnel(funnel = {}) {
  return {
    campaignName: funnel.title || funnel.campaignName || funnel.campaign?.name || '',
    funnelSlug: funnel.funnelSlug || '',
    funnelType: funnel.funnelType || 'quiz-funnel',
    previewRoute: funnel.previewRoute || '',
    previewUrl: funnel.previewUrl || null,
    previewDocument: funnel.previewDocument || '',
    deployUrl: funnel.deployUrl || funnel.deployment?.url || null,
    pagesCreated: Array.isArray(funnel.pagesCreated) ? funnel.pagesCreated : [],
    componentsReused: Array.isArray(funnel.componentsReused) ? funnel.componentsReused : [],
    assumptions: Array.isArray(funnel.assumptions) ? funnel.assumptions : [],
    notes: funnel.notes || '',
    lastEdit: funnel.lastEdit || null,
    createdAt: funnel.createdAt || null,
    updatedAt: funnel.updatedAt || funnel.createdAt || null,
    _slug: funnel.funnelSlug || funnel.id || '',
    _brandSlug: funnel.brand?.slug || '',
    _brandName: funnel.brand?.name || '',
    _offer: funnel.campaign?.offer || '',
    _projectType: funnel.campaign?.projectType || '',
    _saasFunnel: {
      id: funnel.id,
      artifactType: funnel.artifactType,
      workflowJobId: funnel.workflowJobId || null,
      brandId: funnel.brand?.id || null,
      campaignId: funnel.campaign?.id || null,
      deployment: funnel.deployment || null,
    },
  };
}

export function safeFunnelError(error, fallback = 'Funnels could not be loaded. Try again.') {
  const code = String(error?.code || '').toLowerCase();
  if (code === 'funnel_not_found') return 'That funnel is not available in the active company.';
  if (code === 'funnel_content_missing') return 'This funnel record exists, but its generated content is not available yet.';
  if (code === 'funnel_edit_source_unavailable') return 'This preview does not have editable SaaS source metadata yet.';
  if (code === 'funnel_edit_in_progress') return 'That edit is already running. Wait for it to finish before retrying.';
  if (Number(error?.status) === 403) return 'Your company role cannot perform this funnel action.';
  return fallback;
}

export function hasCurrentFunnelData({ data, isSaasSession, dataCompanyId, companyId } = {}) {
  if (!data) return false;
  return !isSaasSession || Boolean(companyId && dataCompanyId === companyId);
}

const funnelsResponsiveStyles = `
  @media (max-width: 900px) {
    .funnels-view-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
  @media (max-width: 640px) {
    .funnels-page,
    .funnels-view-page {
      padding: 24px 16px 48px !important;
    }
    .funnels-page-header {
      align-items: flex-start !important;
    }
    .funnels-page-header h1 {
      font-size: 34px !important;
    }
    .funnels-view-header {
      align-items: flex-start !important;
      gap: 14px !important;
    }
    .funnels-view-header h1 {
      font-size: 32px !important;
    }
    .funnels-browser-toolbar {
      flex-wrap: wrap;
    }
    .funnels-browser-address {
      flex-basis: calc(100% - 50px);
      order: 2;
    }
    .funnels-mobile-preview {
      width: min(380px, 100%) !important;
    }
  }
`;

export default function Funnels(props) {
  if (props.session?.user?.auth?.type === 'supabase') {
    return <SaasFunnelWorkbench {...props} previewSandbox={FUNNEL_PREVIEW_SANDBOX} />;
  }
  return <LegacyFunnels {...props} />;
}

function LegacyFunnels({ navigate, route, session }) {
  const [mode, setMode] = useState('input');
  const [brands, setBrands] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [funnels, setFunnels] = useState(null);
  const [briefs, setBriefs] = useState(null);
  const [data, setData] = useState(null);
  const [dataCompanyId, setDataCompanyId] = useState('');
  const [error, setError] = useState('');
  const [buildingBrief, setBuildingBrief] = useState(null);
  const [buildStatus, setBuildStatus] = useState('');
  const [buildError, setBuildError] = useState('');
  const [pagination, setPagination] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const companyId = activeCompanyId(session);
  const isSaasSession = session?.user?.auth?.type === 'supabase';

  useEffect(() => {
    let active = true;
    if (isSaasSession) {
      setData(null);
      setDataCompanyId('');
      setMode('input');
    }
    setBrands(null);
    setSelectedBrand('');
    setFunnels(null);
    setError('');
    const request = isSaasSession
      ? api.get('/api/saas/brands', tenantHeaders(companyId)).then((response) => response.brands || [])
      : api.get('/api/clients').then((response) => response.clients || []);
    request
      .then((nextBrands) => { if (active) setBrands(nextBrands); })
      .catch((err) => {
        if (!active) return;
        setBrands([]);
        setError(safeFunnelError(err));
      });
    return () => { active = false; };
  }, [isSaasSession, companyId]);

  useEffect(() => {
    let active = true;
    if (!selectedBrand) {
      setFunnels(null);
      setBriefs(null);
      setPagination(null);
      return () => { active = false; };
    }
    setFunnels(null);
    setBriefs(null);
    setPagination(null);
    setError('');
    const funnelRequest = isSaasSession
      ? api.get(
        `/api/saas/funnels?brandId=${encodeURIComponent(selectedBrand)}&limit=50&offset=0`,
        tenantHeaders(companyId),
      )
      : api.get(`/api/funnels/${encodeURIComponent(selectedBrand)}`);
    funnelRequest
      .then((response) => {
        if (!active) return;
        setFunnels(response.funnels || []);
        setPagination(response.pagination || null);
      })
      .catch((err) => {
        if (!active) return;
        setFunnels([]);
        setError(safeFunnelError(err));
      });
    if (!isSaasSession) {
      api.get(`/api/briefs/${encodeURIComponent(selectedBrand)}`)
        .then((response) => { if (active) setBriefs(response.briefs || []); })
        .catch(() => { if (active) setBriefs([]); });
    }
    return () => { active = false; };
  }, [selectedBrand, isSaasSession, companyId]);

  async function buildFromBrief(briefSlug) {
    setBuildingBrief(briefSlug);
    setBuildError('');
    setBuildStatus('Connecting…');
    try {
      const token = getToken();
      const res = await fetch('/api/funnel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ brandSlug: selectedBrand, briefSlug, funnelType: 'quiz-funnel' }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let finalData = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === 'progress' && evt.label) setBuildStatus(evt.label);
          else if (evt.type === 'tool' && (evt.summary || evt.name)) setBuildStatus(evt.summary || evt.name);
          else if (evt.type === 'error') throw new Error(evt.error || evt.message || 'Funnel build failed');
          else if (evt.type === 'result') finalData = evt.funnel || evt.result || evt.data;
        }
      }
      if (!finalData) throw new Error('Stream ended without a final result');
      setBuildingBrief(null);
      setBuildStatus('');
      setData(finalData);
      setDataCompanyId('');
      setMode('result');
    } catch (err) {
      setBuildError(err.message);
      setBuildingBrief(null);
      setBuildStatus('');
    }
  }

  useEffect(() => {
    const brand = route?.query?.brand;
    const funnel = route?.query?.funnel;
    if (!funnel || (!isSaasSession && !brand)) return;
    let active = true;
    setError('');
    setDetailLoading(true);
    if (isSaasSession) {
      setData(null);
      setDataCompanyId('');
      setMode('input');
    }
    const request = isSaasSession
      ? api.get(`/api/saas/funnels/${encodeURIComponent(funnel)}`, tenantHeaders(companyId))
      : api.get(`/api/library/${encodeURIComponent(brand)}/funnel/${encodeURIComponent(funnel)}`);
    request
      .then((r) => {
        if (!active) return;
        const d = isSaasSession ? normalizeSaasFunnel(r.funnel || {}) : (r.data || r);
        setData(d);
        setDataCompanyId(isSaasSession ? companyId : '');
        if (isSaasSession) setSelectedBrand(r.funnel?.brand?.id || '');
        setMode('result');
      })
      .catch((err) => {
        if (!active) return;
        setData(null);
        setDataCompanyId('');
        setMode('input');
        setError(safeFunnelError(err, 'The funnel could not be opened. Try again.'));
      })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [route?.query?.brand, route?.query?.funnel, isSaasSession, companyId]);

  function reset() {
    setMode('input');
    setData(null);
    setDataCompanyId('');
    setError('');
    if (route?.query?.brand || route?.query?.funnel) navigate('funnels');
  }

  async function loadMore() {
    if (!isSaasSession || !selectedBrand || !pagination?.hasMore) return;
    try {
      const response = await api.get(
        `/api/saas/funnels?brandId=${encodeURIComponent(selectedBrand)}&limit=50&offset=${pagination.nextOffset}`,
        tenantHeaders(companyId),
      );
      setFunnels((current) => [...new Map([...(current || []), ...(response.funnels || [])]
        .map((funnel) => [funnel.id, funnel])).values()]);
      setPagination(response.pagination || null);
    } catch (err) {
      setError(safeFunnelError(err));
    }
  }

  if (mode === 'result' && hasCurrentFunnelData({ data, isSaasSession, dataCompanyId, companyId })) {
    return <FunnelView data={data} onReset={reset} navigate={navigate} session={session} />;
  }

  if (isSaasSession && (brands == null || detailLoading)) {
    return (
      <div style={{ padding: '32px 40px 64px', maxWidth: 1100, margin: '0 auto' }}>
        <ProtectedPageLoader
          title="Loading funnels"
          body="Checking your company access and tenant-owned generated experiences."
          minHeight={360}
        />
      </div>
    );
  }

  return (
    <div className="funnels-page" style={{ padding: '32px 40px 64px', maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <style>{funnelsResponsiveStyles}</style>
      <header className="funnels-page-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 40, fontWeight: 400, margin: 0, color: PALETTE.ink, letterSpacing: '-0.02em',
        }}>Funnels</h1>
        <Pill tone="accent">Builder</Pill>
        <div style={{ flex: 1 }} />
        <Button variant="primary" size="sm" onClick={() => navigate('wizard')}>＋ New funnel</Button>
      </header>

      {error && (
        <div style={{
          background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', color: '#8B2E1A',
          borderRadius: 12, padding: '12px 16px', fontSize: 13, marginBottom: 20,
        }}>{error}</div>
      )}

      <Card style={{ padding: 24, marginBottom: 24 }}>
        <Eyebrow style={{ marginBottom: 12 }}>Pick a brand</Eyebrow>
        {brands == null ? (
          <div style={{ color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic' }}>Loading…</div>
        ) : brands.length === 0 ? (
          <div style={{ color: PALETTE.ink3, fontSize: 13 }}>
            No brands yet. <a href="#/brand-research" style={{ color: PALETTE.accent }}>Run brand research first.</a>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {brands.map((b) => {
              const brandKey = isSaasSession ? b.id : b.slug;
              return (
              <button key={brandKey} onClick={() => setSelectedBrand(brandKey)} style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: '12px 14px',
                background: selectedBrand === brandKey ? PALETTE.accentTint : PALETTE.surface,
                border: `1px solid ${selectedBrand === brandKey ? PALETTE.accentSoft : PALETTE.line}`,
                borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                fontFamily: 'Inter, sans-serif',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: PALETTE.surfaceAlt,
                  border: `1px solid ${PALETTE.line}`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 700, color: PALETTE.accent, flexShrink: 0,
                }}>{(b.name || '?').trim().charAt(0).toUpperCase()}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                  <div style={{ fontSize: 11, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.location || b.websiteUrl || b.website || ''}</div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </Card>

      {selectedBrand && briefs != null && briefs.length > 0 && (
        <Card style={{ padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <Eyebrow>Build a funnel from a brief</Eyebrow>
            <span style={{ fontSize: 11.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace' }}>
              Bypasses the wizard — runs the funnel agent directly
            </span>
          </div>
          {buildError && (
            <pre style={{
              background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', color: '#8B2E1A',
              borderRadius: 10, padding: '10px 14px', fontSize: 12,
              fontFamily: 'JetBrains Mono, monospace', marginBottom: 12,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 280, overflow: 'auto',
            }}>{buildError}</pre>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {briefs.map((b) => {
              const slug = b.slug || b.briefSlug;
              const isBuilding = buildingBrief === slug;
              const disabled = !!buildingBrief;
              return (
                <div key={slug} style={{
                  padding: '14px 16px',
                  background: isBuilding ? PALETTE.accentTint : PALETTE.surface,
                  border: `1px solid ${isBuilding ? PALETTE.accentSoft : PALETTE.line}`,
                  borderRadius: 12,
                  opacity: disabled && !isBuilding ? 0.4 : 1,
                  transition: 'all .15s',
                }}>
                  <Eyebrow style={{ fontSize: 9.5, marginBottom: 6 }}>Brief</Eyebrow>
                  <div style={{
                    fontFamily: 'Instrument Serif, serif', fontSize: 17, fontWeight: 400,
                    color: PALETTE.ink, lineHeight: 1.25, marginBottom: 8,
                  }}>{b.campaignName || slug}</div>
                  {isBuilding ? (
                    <div style={{
                      fontSize: 12, color: PALETTE.accent, fontFamily: 'JetBrains Mono, monospace',
                      letterSpacing: '0.04em',
                    }}>
                      ● {buildStatus || 'Working…'}
                    </div>
                  ) : (
                    <Button
                      variant="accent" size="sm"
                      disabled={disabled}
                      onClick={() => buildFromBrief(slug)}
                    >Build funnel →</Button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {selectedBrand && (
        <div>
          <Eyebrow style={{ marginBottom: 12 }}>Funnels for this brand</Eyebrow>
          {funnels == null ? (
            <div style={{ color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic' }}>Loading…</div>
          ) : funnels.length === 0 ? (
            <Card style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: PALETTE.ink2, marginBottom: 12 }}>
                No funnels for this brand yet.
              </div>
              <Button variant="accent" onClick={() => navigate('wizard')}>Build the first funnel →</Button>
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {funnels.map((f) => (
                <a key={f.id || f.slug || f.funnelSlug} href={isSaasSession
                  ? `#/funnels?funnel=${encodeURIComponent(f.id)}`
                  : `#/funnels?brand=${encodeURIComponent(selectedBrand)}&funnel=${encodeURIComponent(f.slug || f.funnelSlug)}`} style={{
                  display: 'block', padding: '14px 16px',
                  background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
                  borderRadius: 12, textDecoration: 'none', color: 'inherit',
                }}>
                  <Eyebrow style={{ fontSize: 9.5, marginBottom: 6 }}>{f.funnelType || 'Funnel'}</Eyebrow>
                  <div style={{
                    fontFamily: 'Instrument Serif, serif', fontSize: 18, fontWeight: 400,
                    color: PALETTE.ink, lineHeight: 1.25, marginBottom: 4,
                  }}>
                    {f.title || f.campaignName || f.slug || f.funnelSlug}
                  </div>
                  {(f.updatedAt || f.createdAt) && (
                    <div style={{ fontSize: 11, color: PALETTE.ink3 }}>
                      Updated {new Date(f.updatedAt || f.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
          {isSaasSession && pagination?.hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
              <Button variant="soft" onClick={loadMore}>Load more funnels</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── STEP DERIVATION ──────────────────────────────────────────────────────
// Map a route segment to a labelled step. Falls back gracefully for unknown
// patterns so a multi-page funnel of any shape still renders.
const STEP_KINDS = [
  { match: /booking/i,     id: 'booking',   label: 'Booking',   Icon: IconCalendar },
  { match: /thank/i,       id: 'thank-you', label: 'Thank You', Icon: IconSparkle },
  { match: /quiz/i,        id: 'quiz',      label: 'Quiz',      Icon: IconQuestion },
  { match: /confirm/i,     id: 'confirm',   label: 'Confirm',   Icon: IconCheck },
];

function deriveSteps(data) {
  const pages = Array.isArray(data?.pagesCreated) ? data.pagesCreated : [];
  if (!pages.length) {
    return [{ id: 'landing', label: 'Landing', Icon: IconHome, route: data?.previewRoute || '' }];
  }
  return pages.map((p, i) => {
    const route = p.route || '';
    const tail = route.split('/').filter(Boolean).pop() || '';
    if (i === 0) {
      return { id: 'landing', label: 'Landing', Icon: IconHome, route };
    }
    for (const kind of STEP_KINDS) {
      if (kind.match.test(tail) || kind.match.test(route)) {
        return { id: kind.id, label: kind.label, Icon: kind.Icon, route };
      }
    }
    return { id: tail || `step-${i}`, label: titleCase(tail) || `Step ${i + 1}`, Icon: IconPage, route };
  });
}

function titleCase(s) {
  if (!s) return '';
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── RESULT VIEW ──────────────────────────────────────────────────────────
function FunnelView({ data: initialData, onReset, navigate, session }) {
  const [data, setData] = useState(initialData);
  const steps = deriveSteps(data);
  const [activeStepId, setActiveStepId] = useState(steps[0]?.id);
  const [viewport, setViewport] = useState('desktop');
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlStatus, setUrlStatus] = useState({ status: 'idle', message: '' });
  const urlSaveInFlightRef = useRef(false);
  const activeStep = steps.find((s) => s.id === activeStepId) || steps[0];

  // Re-sync if the parent provides a new funnel record (e.g. deep-link change).
  useEffect(() => { setData(initialData); }, [initialData]);

  const previewBase = (data.previewUrl || data.deployUrl || '').replace(/\/$/, '');
  const previewBaseRoot = (data.deployUrl || '').replace(/\/$/, '');
  const iframeSrc = activeStep?.route
    ? (previewBaseRoot ? `${previewBaseRoot}${activeStep.route}` : `${previewBase}`)
    : previewBase;
  const previewLabel = (iframeSrc || '').replace(/^https?:\/\//, '');

  async function saveDeployUrl(newUrl) {
    const trimmed = (newUrl || '').trim().replace(/\/+$/, '');
    if (!trimmed || urlSaveInFlightRef.current) return;
    urlSaveInFlightRef.current = true;
    setUrlStatus({ status: 'working', message: 'Saving preview URL…' });
    try {
      const r = await api.patch(
        `/api/funnels/${encodeURIComponent(data._brandSlug)}/${encodeURIComponent(data._slug || data.funnelSlug)}/meta`,
        { deployUrl: trimmed },
      );
      // The endpoint returns the updated meta — merge it into the in-memory record.
      const updated = r.funnel || r;
      setData((prev) => ({
        ...prev,
        deployUrl: updated.deployUrl ?? prev.deployUrl,
        previewUrl: updated.previewUrl ?? (prev.previewRoute ? `${updated.deployUrl}${prev.previewRoute}` : prev.previewUrl),
      }));
      setEditingUrl(false);
      setUrlStatus({ status: 'done', message: 'Preview URL updated.' });
    } catch (err) {
      setUrlStatus({
        status: 'error',
        message: safeFunnelError(err, "Couldn't update the preview URL. Check the URL and try again."),
      });
    } finally {
      urlSaveInFlightRef.current = false;
    }
  }

  return (
    <div style={{
      height: '100vh', overflowY: 'auto', overflowX: 'hidden',
      background: PALETTE.bg, fontFamily: 'Inter, sans-serif',
    }}>
      <style>{funnelsResponsiveStyles}</style>
      <div className="funnels-view-page" style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 40px 48px' }}>
        {/* Header + step pills on a single row */}
        <div className="funnels-view-header" style={{
          display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap',
          marginBottom: 16,
        }}>
          <div style={{ minWidth: 0 }}>
            <Eyebrow style={{ marginBottom: 4 }}>
              Funnels · {data._brandName || data._brandSlug || ''}
            </Eyebrow>
            <h1 style={{
              margin: 0, fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
              fontSize: 36, fontWeight: 400, color: PALETTE.ink, letterSpacing: '-0.02em',
              lineHeight: 1.05,
            }}>
              {shortenCampaign(data.campaignName) || data._slug || 'Funnel'}
            </h1>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            paddingBottom: 4,
          }}>
            {steps.map((s, i) => (
              <Fragment key={s.id}>
                <StepCard
                  step={s}
                  active={s.id === activeStepId}
                  onClick={() => setActiveStepId(s.id)}
                />
                {i < steps.length - 1 && (
                  <span style={{ color: PALETTE.ink3, display: 'inline-flex' }}>
                    <IconChevronRight size={11} />
                  </span>
                )}
              </Fragment>
            ))}
          </div>
        </div>

        {/* Split: preview + chat */}
        <div className="funnels-view-grid" style={{
          display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(320px, 1fr)',
          gap: 14, alignItems: 'stretch',
        }}>
          {/* Preview frame */}
          <BrowserFrame
            url={previewLabel}
            iframeSrc={iframeSrc}
            iframeDocument={data.previewDocument || ''}
            viewport={viewport}
            onViewport={setViewport}
            onOpen={() => iframeSrc && window.open(iframeSrc, '_blank', 'noopener')}
            deployUrl={data.deployUrl || ''}
            editingUrl={editingUrl}
            urlStatus={urlStatus}
            onStartEditUrl={null}
            onCancelEditUrl={() => {
              setEditingUrl(false);
              setUrlStatus({ status: 'idle', message: '' });
            }}
            onSaveUrl={saveDeployUrl}
          />

          {/* Chat panel */}
          <ChatPanel
            data={data}
            activeStep={activeStep}
            session={session}
            onFunnelUpdated={(updated) => setData(normalizeSaasFunnel(updated))}
          />
        </div>

        {/* Footer meta strip */}
        <div style={{
          marginTop: 22, padding: '12px 18px',
          background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.line}`, borderRadius: 12,
          display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center',
        }}>
          {data.componentsReused?.length > 0 && (
            <Fact label="Components reused" value={`${data.componentsReused.length}`} />
          )}
          <Fact label="Pages" value={`${data.pagesCreated?.length ?? steps.length}`} />
          {data.updatedAt && (
            <Fact
              label="Last updated"
              value={new Date(data.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            />
          )}
          {data.agentUsage?.cost_usd != null && (
            <Fact label="Build cost" value={`$${Number(data.agentUsage.cost_usd).toFixed(2)}`} />
          )}
          <div style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" onClick={onReset}>← Back to funnels</Button>
        </div>
      </div>
    </div>
  );
}

function shortenCampaign(name) {
  if (!name) return '';
  // "Under One Roof — Multigenerational Living by OGC" → "Under One Roof"
  return String(name).split(/\s[—–-]\s/)[0];
}

// ─── STEP CARD ────────────────────────────────────────────────────────────
function StepCard({ step, active, onClick }) {
  const { Icon, label } = step;
  return (
    <button onClick={onClick} style={{
      padding: 0, fontFamily: 'Inter, sans-serif',
      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
    }}>
      <div style={{
        position: 'relative',
        background: active ? PALETTE.surface : PALETTE.surfaceAlt,
        border: `1px solid ${active ? PALETTE.accent : PALETTE.line}`,
        borderRadius: 999, padding: '7px 14px 7px 10px',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        color: active ? PALETTE.ink : PALETTE.ink2,
        boxShadow: active ? `0 0 0 2px ${PALETTE.accentTint}` : 'none',
        transition: 'border-color .15s, box-shadow .15s, background .15s',
      }}>
        <span style={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: active ? PALETTE.accentTint : PALETTE.surface,
          color: active ? PALETTE.accentDeep : PALETTE.ink3,
          border: `1px solid ${active ? PALETTE.accentSoft : PALETTE.line}`,
        }}>
          <Icon size={12} />
        </span>
        <span style={{ fontSize: 12.5, fontWeight: active ? 500 : 400, whiteSpace: 'nowrap' }}>
          {label}
        </span>
      </div>
    </button>
  );
}

// ─── BROWSER FRAME ────────────────────────────────────────────────────────
function BrowserFrame({
  url, iframeSrc, iframeDocument, viewport, onViewport, onOpen,
  deployUrl, editingUrl, urlStatus, onStartEditUrl, onCancelEditUrl, onSaveUrl,
}) {
  const [draftUrl, setDraftUrl] = useState(deployUrl || '');
  useEffect(() => { if (editingUrl) setDraftUrl(deployUrl || ''); }, [editingUrl, deployUrl]);

  return (
    <div style={{
      background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
      borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column',
      minHeight: 560,
    }}>
      {/* Toolbar */}
      <div className="funnels-browser-toolbar" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderBottom: `1px solid ${PALETTE.line}`,
        background: PALETTE.surface,
      }}>
        <div style={{ display: 'flex', gap: 5 }}>
          <Dot /><Dot /><Dot />
        </div>
        {editingUrl ? (
          <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              autoFocus
              disabled={urlStatus?.status === 'working'}
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              placeholder="https://historical-preview.example"
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveUrl?.(draftUrl);
                if (e.key === 'Escape') onCancelEditUrl?.();
              }}
              style={{
                flex: 1, padding: '6px 10px', fontSize: 12,
                fontFamily: 'JetBrains Mono, monospace', color: PALETTE.ink,
                background: PALETTE.surface, border: `1px solid ${PALETTE.accent}`,
                boxShadow: `0 0 0 2px ${PALETTE.accentTint}`,
                borderRadius: 7, outline: 'none',
              }}
            />
            <button type="button" disabled={urlStatus?.status === 'working'} onClick={() => onSaveUrl?.(draftUrl)} style={{
              padding: '6px 10px', fontSize: 11, borderRadius: 7,
              background: PALETTE.ink, color: '#FAF8F5',
              border: `1px solid ${PALETTE.ink}`, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
            }}>Save</button>
            <button type="button" disabled={urlStatus?.status === 'working'} onClick={onCancelEditUrl} style={{
              padding: '6px 10px', fontSize: 11, borderRadius: 7,
              background: 'transparent', color: PALETTE.ink2,
              border: `1px solid ${PALETTE.line}`, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
            }}>Cancel</button>
          </div>
        ) : (
          <>
            <div className="funnels-browser-address" style={{
              flex: 1, padding: '5px 12px', borderRadius: 7,
              background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.line}`,
              fontSize: 11.5, color: PALETTE.ink2, fontFamily: 'Inter, sans-serif',
              textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{url || '—'}</div>
            <div style={{
              display: 'flex', gap: 0,
              background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.line}`, borderRadius: 7,
              padding: 2,
            }}>
              <ViewportBtn active={viewport === 'desktop'} onClick={() => onViewport('desktop')}>
                <IconMonitor size={13} />
              </ViewportBtn>
              <ViewportBtn active={viewport === 'mobile'} onClick={() => onViewport('mobile')}>
                <IconPhone size={13} />
              </ViewportBtn>
            </div>
            {onStartEditUrl && <button onClick={onStartEditUrl} title="Edit deploy URL" style={{
              padding: '6px 8px', fontSize: 11, borderRadius: 7,
              border: `1px solid ${PALETTE.line}`, background: PALETTE.surface,
              color: PALETTE.ink2, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              display: 'inline-flex', alignItems: 'center',
            }}><IconPencil size={12} /></button>}
            {iframeSrc && (
              <button onClick={onOpen} style={{
                padding: '6px 10px', fontSize: 11, borderRadius: 7,
                border: `1px solid ${PALETTE.line}`, background: PALETTE.surface,
                color: PALETTE.ink2, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}>Open ↗</button>
            )}
          </>
        )}
      </div>

      {urlStatus?.message && (
        <div
          role={urlStatus.status === 'error' ? 'alert' : 'status'}
          style={{
            padding: '9px 12px',
            borderBottom: `1px solid ${urlStatus.status === 'error' ? PALETTE.warn : PALETTE.line}`,
            background: urlStatus.status === 'error' ? PALETTE.warnSoft : PALETTE.surfaceAlt,
            color: urlStatus.status === 'error' ? PALETTE.warn : PALETTE.ink2,
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {urlStatus.message}
        </div>
      )}

      {/* iframe */}
      <div style={{
        flex: 1, background: PALETTE.surfaceAlt,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: viewport === 'mobile' ? '20px 0' : 0,
        minHeight: 0,
      }}>
        {iframeSrc || iframeDocument ? (
          <iframe
            className={viewport === 'mobile' ? 'funnels-mobile-preview' : undefined}
            {...(iframeSrc ? { src: iframeSrc } : { srcDoc: iframeDocument })}
            title="Funnel preview"
            sandbox={iframeDocument ? '' : FUNNEL_PREVIEW_SANDBOX}
            style={{
              width: viewport === 'mobile' ? 380 : '100%',
              height: viewport === 'mobile' ? 700 : 560,
              border: viewport === 'mobile' ? `1px solid ${PALETTE.line}` : 'none',
              borderRadius: viewport === 'mobile' ? 18 : 0,
              background: '#fff',
            }}
          />
        ) : (
          <div style={{
            color: PALETTE.ink3, fontSize: 13, padding: 40, textAlign: 'center',
          }}>
            No preview URL on this funnel record.
          </div>
        )}
      </div>
    </div>
  );
}

function Dot() {
  return <span style={{ width: 9, height: 9, borderRadius: '50%', background: PALETTE.line }} />;
}
function ViewportBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 9px', borderRadius: 5, border: 'none',
      background: active ? PALETTE.surface : 'transparent',
      color: active ? PALETTE.ink : PALETTE.ink3,
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
      boxShadow: active ? '0 1px 2px rgba(26,23,20,0.06)' : 'none',
    }}>{children}</button>
  );
}

// ─── CHAT PANEL ───────────────────────────────────────────────────────────
const QUICK_PROMPTS = [
  'Make hero shorter',
  'Add more spacing',
  'Tighten the headline',
];

function activeTenantRole(session) {
  const companyId = activeCompanyId(session);
  return session?.user?.memberships?.find((membership) => membership.companyId === companyId)?.role
    || session?.user?.role
    || '';
}

function ChatPanel({ data, activeStep, session, onFunnelUpdated }) {
  const [messages, setMessages] = useState(() => [
    {
      role: 'assistant',
      text: `I've generated the base "${shortenCampaign(data.campaignName) || data._slug}" landing page based on your previous funnels. How does the hero section look?`,
    },
  ]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle'); // idle | working | error
  const [statusLabel, setStatusLabel] = useState('Idle');
  const logRef = useRef(null);
  const isSaasFunnel = Boolean(data._saasFunnel?.id);
  const companyId = activeCompanyId(session);
  const hasEditableSource = !isSaasFunnel || data._saasFunnel?.artifactType === 'funnel_report';
  const canEdit = hasEditableSource
    && (!isSaasFunnel || ['owner', 'admin'].includes(activeTenantRole(session)));

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, status]);

  async function send(text) {
    const trimmed = (text || '').trim();
    if (!trimmed || status === 'working') return;
    setMessages((m) => [...m, { role: 'user', text: trimmed }]);
    setInput('');
    setStatus('working');
    setStatusLabel('Editing');

    try {
      const token = getToken();
      const endpoint = isSaasFunnel
        ? `/api/saas/funnels/${encodeURIComponent(data._saasFunnel.id)}/edits`
        : '/api/funnel/edit';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(isSaasFunnel && companyId ? { 'x-company-id': companyId } : {}),
          ...(isSaasFunnel ? { 'Idempotency-Key': `funnel-edit:${crypto.randomUUID()}` } : {}),
        },
        body: JSON.stringify(isSaasFunnel
          ? { message: trimmed, targetRoute: activeStep?.route }
          : {
              brandSlug: data._brandSlug,
              funnelSlug: data._slug || data.funnelSlug,
              message: trimmed,
              targetRoute: activeStep?.route,
            }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let finalReply = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }

          if (evt.type === 'progress' || evt.type === 'activity') {
            if (evt.label) setStatusLabel(String(evt.label).slice(0, 32));
          } else if (evt.type === 'message' || evt.type === 'assistant' || evt.type === 'text') {
            if (evt.text) finalReply = evt.text;
          } else if (evt.type === 'result') {
            if (evt.funnel && isSaasFunnel) {
              onFunnelUpdated?.(evt.funnel);
              finalReply = evt.funnel.notes || 'Done — the source changes were saved.';
            }
            if (evt.notes) finalReply = evt.notes;
            if (evt.message) finalReply = evt.message;
          } else if (evt.type === 'error') {
            throw new Error(evt.message || evt.error || 'Edit failed');
          }
        }
      }
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: finalReply || 'Done — the source changes were saved. Hosting is not configured.' },
      ]);
      setStatus('idle'); setStatusLabel('Idle');
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', text: `Couldn't apply that edit: ${err.message}`, error: true }]);
      setStatus('error'); setStatusLabel('Error');
      setTimeout(() => { setStatus('idle'); setStatusLabel('Idle'); }, 4000);
    }
  }

  return (
    <div style={{
      background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14,
      display: 'flex', flexDirection: 'column', minHeight: 560,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 16px', borderBottom: `1px solid ${PALETTE.line}`,
      }}>
        <div style={{
          fontFamily: 'Instrument Serif, serif', fontSize: 18, color: PALETTE.ink, letterSpacing: '-0.005em',
        }}>Edit this funnel</div>
        <StatusDot status={status} />
        <div style={{ flex: 1 }} />
        <Eyebrow style={{ color: PALETTE.ink3 }}>{statusLabel}</Eyebrow>
      </div>

      {!hasEditableSource && (
        <div style={{ padding: '12px 16px', background: PALETTE.surfaceAlt, color: PALETTE.ink2, fontSize: 12.5 }}>
          This generated preview is read-only until its workflow produces a funnel report.
        </div>
      )}
      {hasEditableSource && !canEdit && (
        <div style={{ padding: '12px 16px', background: PALETTE.surfaceAlt, color: PALETTE.ink2, fontSize: 12.5 }}>
          Funnel edits are available to company owners and admins.
        </div>
      )}

      {/* Log */}
      <div ref={logRef} style={{
        flex: 1, overflowY: 'auto', padding: '14px 16px',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {messages.map((m, i) => <ChatMessage key={i} role={m.role} text={m.text} error={m.error} />)}
        {status === 'working' && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, color: PALETTE.ink3, fontSize: 12 }}>
            <Beacon size="inline" delayMs={0} slowAfterMs={null} />
            Working — source changes save when done
          </div>
        )}
      </div>

      {/* Quick prompts */}
      <div style={{
        display: 'flex', gap: 6, padding: '10px 16px', flexWrap: 'wrap',
        borderTop: `1px solid ${PALETTE.line}`,
      }}>
        {QUICK_PROMPTS.map((q) => (
          <button key={q} onClick={() => setInput(q)} style={{
            padding: '5px 10px', fontSize: 11.5, borderRadius: 999,
            background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
            color: PALETTE.ink2, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}>{q}</button>
        ))}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        style={{
          display: 'flex', gap: 8, padding: '10px 12px',
          borderTop: `1px solid ${PALETTE.line}`,
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask for changes…"
          disabled={status === 'working' || !canEdit}
          style={{
            flex: 1, padding: '10px 12px', fontSize: 13,
            fontFamily: 'Inter, sans-serif', color: PALETTE.ink,
            background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
            borderRadius: 9, outline: 'none',
          }}
        />
        <button
          type="submit"
          aria-label="Send funnel edit"
          title="Send funnel edit"
          disabled={!input.trim() || status === 'working' || !canEdit}
          style={{
          width: 38, height: 38, padding: 0, borderRadius: 9,
          background: PALETTE.ink, color: '#FAF8F5', border: `1px solid ${PALETTE.ink}`,
          cursor: input.trim() && status !== 'working' && canEdit ? 'pointer' : 'not-allowed',
          opacity: input.trim() && status !== 'working' && canEdit ? 1 : 0.5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <IconArrowUp size={14} />
        </button>
      </form>
    </div>
  );
}

function ChatMessage({ role, text, error }) {
  const isUser = role === 'user';
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      flexDirection: isUser ? 'row-reverse' : 'row',
    }}>
      {!isUser && (
        <div style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: PALETTE.accent, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
        }}>AI</div>
      )}
      <div style={{
        maxWidth: '85%',
        padding: '10px 12px', borderRadius: 10,
        background: isUser ? PALETTE.accentTint : PALETTE.surface,
        border: `1px solid ${error ? '#E8C9C1' : isUser ? PALETTE.accentSoft : PALETTE.line}`,
        color: error ? '#8B2E1A' : PALETTE.ink2,
        fontSize: 13, lineHeight: 1.5,
      }}>
        {text}
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === 'error' ? PALETTE.danger
    : status === 'working' ? PALETTE.warn
    : PALETTE.success;
  return (
    <span style={{ position: 'relative', width: 9, height: 9, display: 'inline-block' }}>
      {status === 'working' && (
        <span style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: color, animation: 'rai-pulse 2s ease-out infinite',
        }}/>
      )}
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: color,
      }}/>
    </span>
  );
}

// ─── SHARED ATOMS ─────────────────────────────────────────────────────────
function Eyebrow({ children, color, style = {} }) {
  return (
    <div style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: color || PALETTE.accent, fontWeight: 500,
      ...style,
    }}>{children}</div>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{
        fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: PALETTE.ink3, fontFamily: 'Inter, sans-serif',
      }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 500, color: PALETTE.ink }}>{value}</div>
    </div>
  );
}

function Fragment({ children }) { return <>{children}</>; }

// ─── ICONS (line-stroke, no fills) ────────────────────────────────────────
function svg(size, children) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}
function IconHome({ size = 16 })       { return svg(size, <><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></>); }
function IconQuestion({ size = 16 })   { return svg(size, <><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7"/><circle cx="12" cy="17" r=".6" fill="currentColor"/></>); }
function IconCalendar({ size = 16 })   { return svg(size, <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>); }
function IconSparkle({ size = 16 })    { return svg(size, <><path d="M5 19l3-3 4 1 5-5 3 3"/><path d="M14 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"/></>); }
function IconPage({ size = 16 })       { return svg(size, <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></>); }
function IconCheck({ size = 16 })      { return svg(size, <><path d="M5 12l5 5L20 7"/></>); }
function IconChevronRight({ size = 16 }){ return svg(size, <><path d="M9 6l6 6-6 6"/></>); }
function IconMonitor({ size = 16 })    { return svg(size, <><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M9 21h6M12 17v4"/></>); }
function IconPhone({ size = 16 })      { return svg(size, <><rect x="7" y="3" width="10" height="18" rx="2"/><path d="M11 18h2"/></>); }
function IconArrowUp({ size = 16 })    { return svg(size, <><path d="M12 19V5M5 12l7-7 7 7"/></>); }
function IconPencil({ size = 16 })     { return svg(size, <><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></>); }
